// ОТКЛЮЧЕНО: при shouldUseNewPermissions() === false хук возвращает все права (canView/canEdit и т.д. = true). Включить после переноса проверок на backend.
import { useSelector } from 'react-redux'
import {
  selectUserPermissions,
  selectUserRole,
  selectHasPermission,
  selectHasSubModulePermission,
} from '../store/slices/userPermissions'
import { shouldUseNewPermissions } from '../utils/devMode'
import {
  getUserDataFromStorage,
  hasPermission as checkPermission,
  hasRole,
  isAdmin,
  getUserRole,
  getUserPermissions,
} from '../utils/permissionsManager'

// Основной хук для работы с правами
export const usePermissions = () => {
  const permissions = useSelector(selectUserPermissions)
  const userRole = useSelector(selectUserRole)
  const useNewPermissions = shouldUseNewPermissions()

  // Если новая система прав отключена, возвращаем безопасные значения
  if (!useNewPermissions) {
    return {
      permissions: null,
      userRole: null,
      hasPermission: () => true, // Разрешаем все для совместимости
      hasSubModulePermission: () => true,
      canView: () => true,
      canEdit: () => true,
      canDelete: () => true,
      canCreate: () => true,
      canExport: () => true,
      canPrint: () => true,
    }
  }

  // Fallback на localStorage если права не загружены в Redux
  const fallbackData = getUserDataFromStorage()
  const finalPermissions =
    permissions || fallbackData.permissions
  const finalUserRole = userRole || fallbackData.role

  return {
    permissions: finalPermissions,
    userRole: finalUserRole,
    hasPermission: (module, action) => {
      return checkPermission(module, action)
    },
    hasSubModulePermission: (module, subModule, action) => {
      return checkPermission(module, action, subModule)
    },
    canView: (module) => checkPermission(module, 'canView'),
    canEdit: (module) => checkPermission(module, 'canEdit'),
    canDelete: (module) =>
      checkPermission(module, 'canDelete'),
    canCreate: (module) =>
      checkPermission(module, 'canCreate'),
    canExport: (module) =>
      checkPermission(module, 'canExport'),
    canPrint: (module) =>
      checkPermission(module, 'canPrint'),
  }
}

// Хук для проверки конкретного права
export const useHasPermission = (module, action) => {
  return useSelector(selectHasPermission(module, action))
}

// Хук для проверки прав на подмодуль
export const useHasSubModulePermission = (
  module,
  subModule,
  action,
) => {
  return useSelector(
    selectHasSubModulePermission(module, subModule, action),
  )
}

// Хук для проверки роли
export const useUserRole = () => {
  return useSelector(selectUserRole)
}

// Хук для проверки является ли пользователь админом
export const useIsAdmin = () => {
  const userRole = useSelector(selectUserRole)
  return userRole === 'admin'
}

// Хук для проверки является ли пользователь драйвером
export const useIsDriver = () => {
  const userRole = useSelector(selectUserRole)
  return userRole === 'driver'
}

// Хук для проверки является ли пользователь пикером
export const useIsPicker = () => {
  const userRole = useSelector(selectUserRole)
  return userRole === 'picker'
}

// Хук для получения роли из localStorage (fallback)
export const useUserRoleFromStorage = () => {
  return getUserRole()
}

// Хук для проверки прав из localStorage (fallback)
export const usePermissionsFromStorage = () => {
  return getUserPermissions()
}

// Хук для проверки является ли пользователь админом (из localStorage)
export const useIsAdminFromStorage = () => {
  return isAdmin()
}

// Хук для проверки роли (из localStorage)
export const useHasRoleFromStorage = (role) => {
  return hasRole(role)
}
